<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { ICorredor } from '../../types/corredor.type';
import { IPagination } from '../../types/pagination.type';
import { useToast } from '../../composables/useToast';
import CorredoresABMRepository from '@repositories/corredoresABM.repository';
import { validationRut } from '../../helpers/validationRUT';
import { useI18n } from "vue-i18n";
import DataTable from "@components/DataTable.vue";

const isLoadingCorredores = ref<boolean>(false);

const { t } = useI18n();
const { $toast } = useToast();
const corredores = ref<ICorredor[]>([]);
const currentPage = ref<number>();
const paginationData = ref<IPagination>();
const modalType = ref<string | null>(null);
const modalTitle = ref<string | undefined>();
const isModalOpen = ref<boolean>(false);
const selectedCorredor = ref<ICorredor | null>(null);
const isViewActive = ref<boolean>(true);
const searchFormRef = ref<any>(null);
const emailList = ref<string[]>([]);
const pendingEmail = ref<string>('');
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatEmails = (emails: ICorredor['emails']) => (
  Array.isArray(emails) ? emails.map((item) => item.email).join(', ') : ''
);

const fieldsTableInactive: any = ref([
  { key: 'id', label: 'Id', sortable: true, thClass: 'col-id', tdClass: 'col-id' },
  { key: 'code', label: t('ajustes.corredores.code'), thClass: 'col-code', tdClass: 'col-code' },
  { key: 'fullname', label: t('ajustes.corredores.nombre_completo'), thClass: 'col-name', tdClass: 'col-name' },
  { key: 'rut', label: t('ajustes.corredores.rut'), thClass: 'col-rut', tdClass: 'col-rut' },
  { key: 'emails', label: t('ajustes.corredores.emails'), thClass: 'col-emails', tdClass: 'col-emails' },
  { key: 'restore', label: t('global.restaurar'), thClass: 'col-action', tdClass: 'col-action' },
]);

const fieldsTableActive: any = ref([
  { key: 'id', label: 'Id', sortable: true, thClass: 'col-id', tdClass: 'col-id' },
  { key: 'code', label: t('ajustes.corredores.code'), thClass: 'col-code', tdClass: 'col-code' },
  { key: 'fullname', label: t('ajustes.corredores.nombre_completo'), thClass: 'col-name', tdClass: 'col-name' },
  { key: 'rut', label: t('ajustes.corredores.rut'), thClass: 'col-rut', tdClass: 'col-rut' },
  { key: 'emails', label: t('ajustes.corredores.emails'), thClass: 'col-emails', tdClass: 'col-emails' },
  { key: 'corredorInfo', label: t('global.editar'), thClass: 'col-action', tdClass: 'col-action' },
  { key: 'delete', label: t('global.eliminar'), thClass: 'col-action', tdClass: 'col-action' },
]);

const fieldsFilter: any = ref([
  {
    key: 'search',
    title: t('global.buscar'),
    type: 'text',
    placeholder: t('ajustes.corredores.buscar_por_nombre'),
    value: null,
    cols: 3,
  },
]);

// API
const getCorredoresFromAPI = async (page: number, deleted: boolean = false, params = {}) => {
  isLoadingCorredores.value = true;
  try {
    const { data } = await CorredoresABMRepository.getCorredoresProPaginated({ page, deleted, ...params });
    if (data && Array.isArray(data?.corredores)) {
      paginationData.value = data.pagination;
      updateCorredores(data.corredores);
      return data.corredores;
    }
    return [];
  } catch (error) {
    console.error('Error while fetching corredores:', error);
    return [];
  } finally {
    isLoadingCorredores.value = false;
  }
};

const updateCorredores = (newCorredores: ICorredor[]) => { corredores.value = newCorredores; };
const loadCorredores = async () => { await getCorredoresFromAPI(1); };

onMounted(() => {
  loadCorredores();
});

// Inputs del modal (sin `code`: lo calcula el backend a partir del rut)
const fieldsInputCorredores = computed(() => {
  return [
    {
      key: 'fullname',
      title: t('ajustes.corredores.nombre_completo'),
      type: 'text',
      validations: { rules: { required: true } },
      placeholder: t('ajustes.corredores.nombre_completo'),
      value: selectedCorredor.value?.fullname,
      cols: 12,
    },
    {
      key: 'rut',
      title: t('ajustes.corredores.rut'),
      type: 'text',
      validations: { rules: { required: true } },
      placeholder: t('ajustes.corredores.rut'),
      value: selectedCorredor.value?.rut,
      cols: 12,
    },
  ];
});

const resetEmails = (emails: ICorredor['emails'] = []) => {
  emailList.value = (emails || []).map((item) => item.email);
  pendingEmail.value = '';
};

const addEmail = () => {
  const email = pendingEmail.value.trim().toLowerCase();
  if (!email) return false;
  if (!EMAIL_REGEX.test(email)) {
    $toast?.error(t('global.email_invalido'));
    return false;
  }
  if (emailList.value.includes(email)) {
    $toast?.error(t('ajustes.corredores.email_duplicado'));
    return false;
  }
  emailList.value = [...emailList.value, email];
  pendingEmail.value = '';
  return true;
};

const removeEmail = (email: string) => {
  emailList.value = emailList.value.filter((item) => item !== email);
};

const commitPendingEmail = () => {
  if (!pendingEmail.value.trim()) return true;
  return addEmail();
};

// UI
const onChangePage = async (page: number) => {
  getCorredoresFromAPI(page);
  currentPage.value = page;
};

const handleOpenModalCreate = () => {
  modalTitle.value = t('ajustes.corredores.agregar_corredor');
  isModalOpen.value = true;
  modalType.value = 'modalCreateCorredor';
  resetEmails();
};

const handleClose = () => {
  modalTitle.value = undefined;
  isModalOpen.value = false;
  modalType.value = null;
  selectedCorredor.value = null;
  resetEmails();
};

const handleActive = async () => {
  const corredoresData = await getCorredoresFromAPI(1);
  updateCorredores(corredoresData);
  isViewActive.value = true;
};

const handleInactive = async () => {
  const corredoresDeleted = await getCorredoresFromAPI(1, true);
  updateCorredores(corredoresDeleted);
  isViewActive.value = false;
};

const handleOpenModalDialog = (item: ICorredor) => {
  modalTitle.value = isViewActive.value
    ? `${t('ajustes.corredores.msg_borrar_corredor')} ${item.id}`
    : `${t('ajustes.corredores.msg_restaurar_corredor')} ${item.id}`;
  modalType.value = 'modalDialog';
  selectedCorredor.value = item;
  isModalOpen.value = true;
};

// Filter Functions
const handleOnSubmit = async ({ items }: any) => {
  getCorredoresFromAPI(1, false, items);
};

const handleReset = async () => {
  if (!searchFormRef.value) return;
  await searchFormRef.value.resetValues();
  getCorredoresFromAPI(1, false);
};

// CRUD
const handleSelectCorredor = (corredorData: ICorredor) => {
  selectedCorredor.value = corredorData;
  resetEmails(corredorData.emails);
  modalTitle.value = t('ajustes.corredores.actualizar_corredor');
  modalType.value = 'modalUpdateCorredor';
  isModalOpen.value = true;
};

const handleSubmitCorredor = async ({ items, isFormValid }: any) => {
  if (!isFormValid) {
    $toast?.error(t('global.debes_completar_los_campos_requeridos'));
    return;
  }
  if (!commitPendingEmail()) return;
  if (!validationRut(items.rut)) {
    $toast?.error(t('ajustes.corredores.rut_invalido'));
    return;
  }

  const isUpdate = modalType.value === 'modalUpdateCorredor';
  if (isUpdate && !selectedCorredor.value) return;

  try {
    if (isUpdate && selectedCorredor.value) {
      await CorredoresABMRepository.updateCorredor({
        ...items,
        id: selectedCorredor.value.id,
        emails: emailList.value,
      });
      $toast?.success(t('ajustes.corredores.msg_actualizado'));
    } else {
      await CorredoresABMRepository.createCorredor({ ...items, emails: emailList.value });
      $toast?.success(t('ajustes.corredores.msg_agregar_corredor'));
    }
  } catch (error: any) {
    $toast?.error(
      error?.message
      || (isUpdate ? t('ajustes.corredores.msg_error_actualizacion') : t('ajustes.corredores.msg_error_creacion')),
    );
    return;
  }
  getCorredoresFromAPI(paginationData.value?.current_page ?? 1, !isViewActive.value);
  handleClose();
};

const handleDeleteRestoreCorredor = async () => {
  if (selectedCorredor?.value?.id) {
    try {
      if (isViewActive.value) {
        await CorredoresABMRepository.deleteCorredor(selectedCorredor.value.id);
      } else {
        await CorredoresABMRepository.restoreCorredor(selectedCorredor.value.id);
      }
    } catch (error) {
      if (isViewActive.value) {
        return $toast?.error(t('ajustes.corredores.msg_error_eliminar'));
      }
      $toast?.error(t('ajustes.corredores.msg_error_restaurar'));
    }
    getCorredoresFromAPI(paginationData.value?.current_page ?? 1, !isViewActive.value);
    handleClose();
  }
};
</script>

<template>
  <Layout title="Corredores" :textColor="'color-secondary'">
    <template #layout>
      <div class="d-flex justify-content-between">
        <div>
          <CButton class="background-blue button__form" style="padding: 0.5rem !important; margin-right: 15px" type="button" @click="handleActive">
            <template #content>{{ t('global.activos') }}</template>
          </CButton>
          <CButton class="background-pink button__form" style="padding: 0.5rem !important" type="button" @click="handleInactive">
            <template #content>{{ t('global.inactivos') }}</template>
          </CButton>
        </div>
        <CButton class="background-pink button__form" style="padding: 0.5rem !important" type="button" @click="handleOpenModalCreate">
          <template #content>{{ t('ajustes.corredores.agregar_corredor') }}</template>
        </CButton>
      </div>

      <div class="form__logEmail">
        <Form ref="searchFormRef" @onSubmit="handleOnSubmit" :inputs="fieldsFilter" customClass="poliza__form">
          <template #buttons>
            <CButton class="background-blue button__form" style="padding: 0.6rem !important; margin: 0.6rem">
              <template #content>{{ t('global.buscar') }}</template>
            </CButton>
            <CButton class="background-blue button__form" style="padding: 0.6rem !important; margin: 0.6rem" type="button" @click="handleReset">
              <template #content>{{ t('global.limpiar_filtro') }}</template>
            </CButton>
          </template>
        </Form>
      </div>

      <div class="corredores-table">
      <DataTable
        :loading="isLoadingCorredores"
        :fields="isViewActive ? fieldsTableActive : fieldsTableInactive"
        :items="corredores"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-slot:cell(emails)="row">
          <div
            class="corredor-table-emails"
            :title="formatEmails(row.data.item.emails)"
          >
            <span
              v-for="email in (row.data.item.emails || [])"
              :key="email.id || email.email"
              class="corredor-table-emails__chip"
            >
              {{ email.email }}
            </span>
            <span v-if="!row.data.item.emails?.length">—</span>
          </div>
        </template>
        <template v-slot:cell(corredorInfo)="corredorData">
          <div @click="handleSelectCorredor(corredorData.data.item)" class="button-icon">
            <img src="/icons/editar.svg" />
          </div>
        </template>
        <template v-slot:cell(delete)="operationsData">
          <div @click="handleOpenModalDialog(operationsData.data.item)" class="button-icon">
            <img src="/icons/basura.svg" alt="eliminar" />
          </div>
        </template>
        <template v-slot:cell(restore)="operationsData">
          <div @click="handleOpenModalDialog(operationsData.data.item)" class="button-icon restore">
            <img src="/icons/restaurar.svg" alt="restore" />
          </div>
        </template>
      </DataTable>
      </div>

      <BModal :modelValue="isModalOpen" hide-footer :centered="true" @hide="handleClose" static :title="modalTitle">
        <template v-if="modalType === 'modalCreateCorredor' || modalType === 'modalUpdateCorredor'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputCorredores"
            @onSubmit="handleSubmitCorredor"
          >
            <template #buttons>
              <div class="corredor-emails mb-3">
                <label class="form-label">{{ t('ajustes.corredores.emails') }}</label>
                <div class="corredor-emails__row">
                  <input
                    v-model="pendingEmail"
                    type="text"
                    inputmode="email"
                    autocomplete="off"
                    class="corredor-emails__input"
                    :placeholder="t('ajustes.corredores.placeholder_email')"
                    @keydown.enter.prevent="addEmail"
                  />
                  <button
                    type="button"
                    class="corredor-emails__add-btn"
                    @click="addEmail"
                  >
                    {{ t('ajustes.corredores.agregar_email') }}
                  </button>
                </div>
                <div v-if="emailList.length" class="corredor-emails__list mt-2">
                  <span
                    v-for="email in emailList"
                    :key="email"
                    class="corredor-emails__chip"
                  >
                    {{ email }}
                    <button type="button" class="corredor-emails__remove" @click="removeEmail(email)">
                      ×
                    </button>
                  </span>
                </div>
              </div>
              <div class="d-flex">
                <CButton type="submit" class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>
                        {{
                          modalType === 'modalCreateCorredor'
                            ? t('ajustes.corredores.agregar_corredor')
                            : t('global.actualizar')
                        }}
                      </span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalDialog'">
          <h5 class="text-center">
            {{
              isViewActive
                ? `¿Está seguro que desea borrar el corredor ${selectedCorredor?.fullname}?`
                : `¿Está seguro que desea restaurar el corredor ${selectedCorredor?.fullname}?`
            }}
          </h5>
          <div class="d-flex mt-3">
            <CButton class="w-100 fs-6 p-2" background="background-pink" @click="handleDeleteRestoreCorredor">
              <template #content><div><span>{{ t('global.confirmar') }}</span></div></template>
            </CButton>
          </div>
        </template>
      </BModal>
    </template>
  </Layout>
</template>

<style scoped>
.corredores-table :deep(table) {
  table-layout: fixed;
  width: 100%;
}

.corredores-table :deep(th),
.corredores-table :deep(td) {
  vertical-align: middle;
}

.corredores-table :deep(.col-id) {
  width: 64px;
}

.corredores-table :deep(.col-code) {
  width: 130px;
}

.corredores-table :deep(.col-name) {
  width: 28%;
}

.corredores-table :deep(.col-rut) {
  width: 130px;
}

.corredores-table :deep(.col-action) {
  width: 76px;
  text-align: center;
}

.corredores-table :deep(.col-code),
.corredores-table :deep(.col-name),
.corredores-table :deep(.col-rut) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.corredor-table-emails {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
}

.corredor-table-emails__chip {
  display: inline-block;
  min-width: 0;
  max-width: 100%;
  padding: 0.1rem 0.5rem;
  overflow: hidden;
  background: #eef3f8;
  border-radius: 999px;
  font-size: 0.78rem;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.corredor-emails__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.corredor-emails__input {
  display: block;
  width: 100%;
  height: 37px;
  padding: 0 12px;
  font-size: 14px;
  font-weight: 700;
  color: #495057;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 20px;
}

.corredor-emails__input:focus {
  outline: none;
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
}

.corredor-emails__add-btn {
  flex-shrink: 0;
  height: 37px;
  padding: 0 1rem;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  background-color: #40a4c6;
  border: 0;
  border-radius: 20px;
  cursor: pointer;
}

.corredor-emails__list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.corredor-emails__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.5rem;
  background: #eef3f8;
  border-radius: 999px;
  font-size: 0.85rem;
}

.corredor-emails__remove {
  border: 0;
  background: transparent;
  line-height: 1;
  padding: 0;
  cursor: pointer;
  font-size: 1rem;
}
</style>
